// ============================================================
//  dialog.ts — Electron-compatible modal replacements
//
//  PROBLEM: window.confirm / window.prompt / window.alert in
//  Electron use OS-level NATIVE dialogs that STEAL renderer focus.
//  After dismissal, focus does NOT reliably return to the renderer's
//  document — user must alt-tab / minimize-restore to type again.
//  This is a KNOWN, CRITICAL Electron bug.
//
//  SOLUTION: All three calls replaced with DOM-based modals that
//  live INSIDE the renderer — no native dialog, no focus loss.
//
//  Usage:
//      import dialog from "../../utils/dialog";
//
//      // Replace:  if (!window.confirm("Sure?")) return;
//      // With:     if (!await dialog.confirm("Sure?")) return;
//
//      // Replace:  const r = window.prompt("Reason?");
//      // With:     const r = await dialog.prompt("Reason?");
//
//      // Replace:  window.alert("Saved");
//      // With:     await dialog.alert("Saved");
//
//  All three return Promises. Calling function MUST be `async`.
// ============================================================

type ConfirmOpts = {
  okText?: string;
  cancelText?: string;
  danger?: boolean;     // red OK button (for delete / cancel actions)
  title?: string;
};
type PromptOpts = ConfirmOpts & {
  inputType?: "text" | "number" | "date";
  multiline?: boolean;
  required?: boolean;
};
type AlertOpts = { okText?: string; title?: string; tone?: "info" | "warn" | "error" };

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

function buildShell(headerText: string, headerBg: string): {
  overlay: HTMLDivElement; body: HTMLDivElement; btnRow: HTMLDivElement;
  cleanup: () => void; addBtn: (label: string, primary: boolean, danger: boolean, onClick: () => void) => HTMLButtonElement;
  prevActive: HTMLElement | null;
} {
  const prevActive = (document.activeElement as HTMLElement | null) || null;

  const overlay = document.createElement("div");
  overlay.setAttribute("data-app-modal", "1");
  overlay.style.cssText = `
    position: fixed; inset: 0; background: rgba(0,0,0,0.55);
    display: flex; align-items: center; justify-content: center;
    z-index: 99999; font-family: ${FONT};
  `;

  const modal = document.createElement("div");
  modal.style.cssText = `
    background: #fff; border-radius: 8px; width: 480px; max-width: 90vw;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3); overflow: hidden;
  `;

  const header = document.createElement("div");
  header.style.cssText = `
    background: ${headerBg}; color: #fff; padding: 12px 16px;
    font-size: 14px; font-weight: 700;
  `;
  header.textContent = headerText;
  modal.appendChild(header);

  const body = document.createElement("div");
  body.style.cssText = "padding: 16px;";
  modal.appendChild(body);

  const btnRow = document.createElement("div");
  btnRow.style.cssText = "display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px;";
  body.appendChild(btnRow);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  const cleanup = () => {
    try { document.body.removeChild(overlay); } catch { /* ignore */ }
    // Restore focus to the element that was active before the modal opened.
    // This is what makes typing work right after the modal closes.
    setTimeout(() => {
      try {
        if (prevActive && typeof prevActive.focus === "function" && document.contains(prevActive)) {
          prevActive.focus();
        } else {
          (document.body as any)?.focus?.();
        }
        window.focus();
      } catch { /* ignore */ }
    }, 0);
  };

  const addBtn = (label: string, primary: boolean, danger: boolean, onClick: () => void) => {
    const btn = document.createElement("button");
    btn.textContent = label;
    const bg = danger ? "#DC2626" : (primary ? "#1D4ED8" : "#F7F8FA");
    const color = (danger || primary) ? "#fff" : "#1A202C";
    const border = (danger || primary) ? "none" : "1px solid #DDE1E7";
    btn.style.cssText = `
      background: ${bg}; color: ${color}; border: ${border};
      border-radius: 4px; padding: 7px 14px; font-size: 12px; font-weight: 600;
      cursor: pointer;
    `;
    btn.addEventListener("click", onClick);
    btnRow.appendChild(btn);
    return btn;
  };

  return { overlay, body, btnRow, cleanup, addBtn, prevActive };
}

export const dialog = {
  /**
   * Async replacement for window.confirm(). Returns Promise<boolean>.
   *  - Renders inside the renderer (no native focus loss).
   *  - ESC = cancel. Enter = OK.
   */
  confirm(message: string, opts?: ConfirmOpts): Promise<boolean> {
    return new Promise((resolve) => {
      const headerBg = opts?.danger ? "#991B1B" : "#2D3748";
      const headerText = opts?.title || (opts?.danger ? "⚠ Confirm" : "❓ Confirm");
      const { overlay, body, cleanup, addBtn } = buildShell(headerText, headerBg);

      const msgDiv = document.createElement("div");
      msgDiv.style.cssText = "font-size: 13px; color: #1A202C; line-height: 1.5; white-space: pre-wrap; margin-bottom: 6px;";
      msgDiv.textContent = message;
      body.insertBefore(msgDiv, body.firstChild);

      let resolved = false;
      const finish = (val: boolean) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve(val);
      };

      const cancelBtn = addBtn(opts?.cancelText || "Cancel", false, false, () => finish(false));
      const okBtn     = addBtn(opts?.okText     || "OK",     !opts?.danger, !!opts?.danger, () => finish(true));

      overlay.addEventListener("click", (e) => { if (e.target === overlay) finish(false); });
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { e.preventDefault(); finish(false); }
        else if (e.key === "Enter") { e.preventDefault(); finish(true); }
      };
      window.addEventListener("keydown", onKey);
      const _origCleanup = cleanup;
      // Wrap cleanup to also remove the keydown listener
      const wrappedCleanup = () => { window.removeEventListener("keydown", onKey); _origCleanup(); };
      cancelBtn.addEventListener("click", () => window.removeEventListener("keydown", onKey));
      okBtn.addEventListener("click", () => window.removeEventListener("keydown", onKey));
      void wrappedCleanup; // keep the reference; cleanup() already runs via finish()

      // Focus the OK button by default
      setTimeout(() => okBtn.focus(), 0);
    });
  },

  /**
   * Async replacement for window.prompt(). Returns Promise<string | null>.
   *  - null when cancelled.
   *  - Empty string when user submits empty (unless required).
   */
  prompt(message: string, defaultValue: string = "", opts?: PromptOpts): Promise<string | null> {
    return new Promise((resolve) => {
      const headerBg = "#2D3748";
      const headerText = opts?.title || "❓ Input Required";
      const { overlay, body, cleanup, addBtn } = buildShell(headerText, headerBg);

      const msgDiv = document.createElement("div");
      msgDiv.style.cssText = "font-size: 13px; color: #1A202C; margin-bottom: 10px; white-space: pre-wrap;";
      msgDiv.textContent = message;
      body.insertBefore(msgDiv, body.firstChild);

      let inputEl: HTMLInputElement | HTMLTextAreaElement;
      if (opts?.multiline) {
        const ta = document.createElement("textarea");
        ta.value = defaultValue;
        ta.rows = 4;
        ta.style.cssText = `
          width: 100%; padding: 8px 10px; border: 1px solid #DDE1E7;
          border-radius: 4px; font-size: 13px; box-sizing: border-box;
          font-family: ${FONT}; resize: vertical; min-height: 70px;
        `;
        inputEl = ta;
      } else {
        const inp = document.createElement("input");
        inp.type = opts?.inputType || "text";
        inp.value = defaultValue;
        inp.style.cssText = `
          width: 100%; padding: 8px 10px; border: 1px solid #DDE1E7;
          border-radius: 4px; font-size: 13px; box-sizing: border-box;
        `;
        inputEl = inp;
      }
      // Insert before the btnRow
      body.insertBefore(inputEl, body.lastChild);

      const errDiv = document.createElement("div");
      errDiv.style.cssText = "color:#DC2626; font-size: 11px; margin-top: 4px; min-height: 14px;";
      body.insertBefore(errDiv, body.lastChild);

      let resolved = false;
      const finish = (val: string | null) => {
        if (resolved) return;
        if (val !== null && opts?.required && !val.trim()) {
          errDiv.textContent = "This field is required";
          inputEl.focus();
          return;
        }
        resolved = true;
        cleanup();
        resolve(val);
      };

      const cancelBtn = addBtn(opts?.cancelText || "Cancel", false, false, () => finish(null));
      const okBtn     = addBtn(opts?.okText     || "OK",     !opts?.danger, !!opts?.danger, () => finish(inputEl.value));

      overlay.addEventListener("click", (e) => { if (e.target === overlay) finish(null); });
      const onKey = ((e: KeyboardEvent) => {
        if (e.key === "Escape") { e.preventDefault(); finish(null); }
        else if (e.key === "Enter" && !opts?.multiline) { e.preventDefault(); finish(inputEl.value); }
      }) as EventListener;
      inputEl.addEventListener("keydown", onKey);
      cancelBtn.addEventListener("click", () => inputEl.removeEventListener("keydown", onKey));
      okBtn.addEventListener("click", () => inputEl.removeEventListener("keydown", onKey));

      // Focus + select the input value
      setTimeout(() => {
        inputEl.focus();
        if ("select" in inputEl) (inputEl as HTMLInputElement).select();
      }, 0);
    });
  },

  /**
   * Async replacement for window.alert(). Returns Promise<void> after dismissal.
   */
  alert(message: string, opts?: AlertOpts): Promise<void> {
    return new Promise((resolve) => {
      const headerBg = opts?.tone === "error" ? "#991B1B" : opts?.tone === "warn" ? "#92400E" : "#2D3748";
      const headerText = opts?.title || (opts?.tone === "error" ? "⚠ Error" : opts?.tone === "warn" ? "⚠ Warning" : "ℹ Notice");
      const { overlay, body, cleanup, addBtn } = buildShell(headerText, headerBg);

      const msgDiv = document.createElement("div");
      msgDiv.style.cssText = "font-size: 13px; color: #1A202C; line-height: 1.5; white-space: pre-wrap; margin-bottom: 6px;";
      msgDiv.textContent = message;
      body.insertBefore(msgDiv, body.firstChild);

      let resolved = false;
      const finish = () => { if (resolved) return; resolved = true; cleanup(); resolve(); };
      const okBtn = addBtn(opts?.okText || "OK", true, false, finish);
      overlay.addEventListener("click", (e) => { if (e.target === overlay) finish(); });
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape" || e.key === "Enter") { e.preventDefault(); finish(); }
      };
      window.addEventListener("keydown", onKey);
      okBtn.addEventListener("click", () => window.removeEventListener("keydown", onKey));
      setTimeout(() => okBtn.focus(), 0);
    });
  },
};

export default dialog;
